import { z } from "zod";
import {
  supportAuthorKindSchema,
  supportReportAnchorSchema,
  supportReportReasonSchema,
  supportSeveritySchema,
  supportTicketKindSchema,
  supportTicketStatusSchema,
} from "../enums.js";
import type { SupportReportAnchor, SupportReportReason } from "../enums.js";
import { isoDate } from "./common.js";

/**
 * Which reasons apply to which anchor. A reason that cannot be true of the
 * anchored unit — "wrong citation" on an analysis that never ran — is noise in
 * the inbox and costs the reporter exactly the hesitation this feature removes.
 * Exported so the browser renders the same set the server enforces.
 */
export const REASONS_BY_ANCHOR: Record<SupportReportAnchor, readonly SupportReportReason[]> = {
  REVIEW: [
    "MISSED_SOMETHING",
    "NOT_A_VIOLATION",
    "WRONG_SEVERITY",
    "ANALYSIS_FAILED",
    "EXPLANATION_UNCLEAR",
    "OTHER",
  ],
  AI_SUMMARY: ["MISSED_SOMETHING", "NOT_A_VIOLATION", "EXPLANATION_UNCLEAR", "OTHER"],
  CHECKLIST_ITEM: [
    "NOT_A_VIOLATION",
    "MISSED_SOMETHING",
    "WRONG_CITATION",
    "EXPLANATION_UNCLEAR",
    "OTHER",
  ],
  FINDING: [
    "NOT_A_VIOLATION",
    "WRONG_CITATION",
    "WRONG_SEVERITY",
    "EXPLANATION_UNCLEAR",
    "OTHER",
  ],
  ANALYSIS_FAILURE: ["ANALYSIS_FAILED", "OTHER"],
};

/**
 * What the reporter types — which, for a report anchored to an AI result, is
 * nothing. Everything else on a ticket (the review's stage, its job error, the
 * frozen source set) is resolved on the server from the ids below, so a
 * reporter never has to know which details matter. A one-click `reason` stands
 * in for `observed`: the server composes the sentence, because the reason is a
 * controlled value the inbox can aggregate and prose is not.
 */
export const createSupportTicketSchema = z
  .object({
    kind: supportTicketKindSchema,
    severity: supportSeveritySchema.default("DEGRADED"),
    subject: z.string().trim().min(1).max(200),
    /** What the reporter expected to happen. */
    expected: z.string().trim().max(4_000).optional(),
    /** What actually happened instead. Optional when a `reason` is given. */
    observed: z.string().trim().min(1).max(4_000).optional(),
    /** Which AI-generated unit the report is anchored to. */
    anchor: supportReportAnchorSchema.optional(),
    /** The one-click cause, replacing free-text `observed`. */
    reason: supportReportReasonSchema.optional(),
    /** Context anchors. The server validates each against the reporter's org. */
    reviewId: z.string().min(1).optional(),
    findingId: z.string().min(1).optional(),
    /** Identifies one checklist row of the analysis run. */
    checklistReferenceId: z.string().min(1).optional(),
    /** The SPA route the reporter was on, for problems with no review to anchor to. */
    pagePath: z.string().max(500).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.kind === "DISPUTED_RESULT" && !value.reviewId) {
      ctx.addIssue({
        code: "custom",
        path: ["reviewId"],
        message: "A disputed result must name the review it disputes.",
      });
    }
    if (value.findingId && !value.reviewId) {
      ctx.addIssue({
        code: "custom",
        path: ["reviewId"],
        message: "A disputed finding must name its review.",
      });
    }
    if (value.checklistReferenceId && !value.reviewId) {
      ctx.addIssue({
        code: "custom",
        path: ["reviewId"],
        message: "A disputed checklist item must name its review.",
      });
    }
    // A report has to say something. Either the reporter picked a reason or
    // they typed what happened; neither one alone can be dropped.
    if (!value.reason && !value.observed) {
      ctx.addIssue({
        code: "custom",
        path: ["observed"],
        message: "Pick a reason or describe what happened.",
      });
    }
    if (value.anchor && value.anchor !== "REVIEW" && !value.reviewId) {
      ctx.addIssue({
        code: "custom",
        path: ["reviewId"],
        message: "An anchored report must name its review.",
      });
    }
    if (value.anchor === "CHECKLIST_ITEM" && !value.checklistReferenceId) {
      ctx.addIssue({
        code: "custom",
        path: ["checklistReferenceId"],
        message: "A checklist report must name the reference it disputes.",
      });
    }
    if (value.anchor && value.reason && !REASONS_BY_ANCHOR[value.anchor].includes(value.reason)) {
      ctx.addIssue({
        code: "custom",
        path: ["reason"],
        message: "That reason does not apply to this kind of result.",
      });
    }
  });
export type CreateSupportTicketInput = z.input<typeof createSupportTicketSchema>;

export const supportMessageSchema = z.object({
  id: z.string(),
  authorKind: supportAuthorKindSchema,
  authorEmail: z.string().nullable(),
  body: z.string(),
  createdAt: isoDate,
});
export type SupportMessage = z.infer<typeof supportMessageSchema>;

export const createSupportMessageSchema = z.object({
  body: z.string().trim().min(1).max(4_000),
});
export type CreateSupportMessageInput = z.infer<typeof createSupportMessageSchema>;

/**
 * The server-assembled context bundle. Every field is derived from the
 * referenced records at report time; none of it is accepted from the browser.
 * It deliberately carries ids, statuses, and counts only — never advertising
 * copy, file contents, or credentials, matching the audit metadata rule.
 */
export const supportDiagnosticsSchema = z.object({
  reviewTitle: z.string().nullable(),
  reviewStatus: z.string().nullable(),
  riskStatus: z.string().nullable(),
  routingOutcome: z.string().nullable(),
  routingReason: z.string().nullable(),
  channelName: z.string().nullable(),
  categoryName: z.string().nullable(),
  submittedAt: isoDate.nullable(),
  /** Latest revision of the material the reporter was looking at. */
  revisionNumber: z.number().int().nullable(),
  materialProcessingStatus: z.string().nullable(),
  fileName: z.string().nullable(),
  /** Analysis queue state — the first thing to check for a stuck review. */
  progressStage: z.string().nullable(),
  jobStatus: z.string().nullable(),
  jobAttempts: z.number().int().nullable(),
  jobMaxAttempts: z.number().int().nullable(),
  jobLastError: z.string().nullable(),
  /** Which frozen source set the run used, so a wrong result is reproducible. */
  sourceSetFingerprint: z.string().nullable(),
  sourceCount: z.number().int().nullable(),
  analysisRunId: z.string().nullable(),
  analysisStatus: z.string().nullable(),
  analysisErrorMessage: z.string().nullable(),
  /** False means the AI response failed schema/citation validation and fell safe. */
  aiSchemaValid: z.boolean().nullable(),
  /** Findings the validator discarded — the usual cause of a "it missed something" report. */
  rejectedFindingCount: z.number().int().nullable(),
  findingCount: z.number().int().nullable(),
  /** Present only when the ticket disputes one checklist row. */
  checklistReferenceId: z.string().nullable(),
  checklistTitle: z.string().nullable(),
  checklistVerdict: z.string().nullable(),
  /** Present only when the ticket disputes one specific finding. */
  findingTitle: z.string().nullable(),
  findingSeverity: z.string().nullable(),
  findingDetectionMethod: z.string().nullable(),
  findingCitationTitle: z.string().nullable(),
  findingRuleVersionId: z.string().nullable(),
  findingPolicyVersionId: z.string().nullable(),
  /** Where the reporter was and what client they used. */
  pagePath: z.string().nullable(),
  reporterRole: z.string().nullable(),
  appVersion: z.string().nullable(),
  collectedAt: isoDate,
});
export type SupportDiagnostics = z.infer<typeof supportDiagnosticsSchema>;

/**
 * The AI result exactly as the reporter was looking at it, frozen when the
 * report was raised.
 *
 * This is a deliberate, bounded exception to the ids-only rule that governs
 * `supportDiagnosticsSchema`, and it exists because a rerun reuses the same
 * `AnalysisRun` row and deletes every finding under it: without this, the
 * sentence the client disputed is gone by the time anyone reads the ticket, and
 * `findingId` points at nothing. Only the disputed unit is copied — the run's
 * own summary, the one checklist row, the one finding. The material text and
 * every other finding stay behind the live inspection endpoint, which reads
 * them under the Super Admin's platform role and writes an audit event.
 */
export const supportDisputedResultSchema = z.object({
  riskStatus: z.string().nullable(),
  aiSummary: z.string().nullable(),
  /** What the model reported the material to be — the check that an image was read at all. */
  materialOverview: z.string().nullable(),
  recommendedAction: z.string().nullable(),
  checklistTitle: z.string().nullable(),
  checklistVerdict: z.string().nullable(),
  checklistNote: z.string().nullable(),
  findingTitle: z.string().nullable(),
  findingExplanation: z.string().nullable(),
  /** The span of the material the finding claims to be about. */
  findingEvidenceQuote: z.string().nullable(),
  findingEvidenceType: z.string().nullable(),
  findingSeverity: z.string().nullable(),
  findingConfidence: z.string().nullable(),
  findingDetectionMethod: z.string().nullable(),
  findingCitationType: z.string().nullable(),
  findingCitationTitle: z.string().nullable(),
  findingCitationLocator: z.string().nullable(),
  findingPageNumber: z.number().int().nullable(),
  findingSuggestedRevision: z.string().nullable(),
  capturedAt: isoDate,
});
export type SupportDisputedResult = z.infer<typeof supportDisputedResultSchema>;

export const supportTicketSummarySchema = z.object({
  id: z.string(),
  reference: z.string(),
  organizationId: z.string(),
  organizationName: z.string(),
  kind: supportTicketKindSchema,
  status: supportTicketStatusSchema,
  severity: supportSeveritySchema,
  /** Null on tickets raised before one-click reasons, and on auto-opened ones. */
  reason: supportReportReasonSchema.nullable(),
  subject: z.string(),
  reporterEmail: z.string(),
  reviewId: z.string().nullable(),
  autoOpened: z.boolean(),
  messageCount: z.number().int().nonnegative(),
  createdAt: isoDate,
  updatedAt: isoDate,
  /** How long the ticket has been open, so the inbox can sort by staleness. */
  ageSeconds: z.number().int().nonnegative(),
  firstResponseAt: isoDate.nullable(),
  resolvedAt: isoDate.nullable(),
  /**
   * Who last wrote in the thread. Null when nobody has — the list can then
   * highlight an incoming reply without opening every ticket.
   */
  lastAuthorKind: supportAuthorKindSchema.nullable(),
  /** Email of that last author, so a colleague's reply is unread even if it was stored as REPORTER. */
  lastAuthorEmail: z.string().nullable(),
});
export type SupportTicketSummary = z.infer<typeof supportTicketSummarySchema>;

export const supportTicketDetailSchema = supportTicketSummarySchema.extend({
  expected: z.string().nullable(),
  observed: z.string(),
  anchor: supportReportAnchorSchema.nullable(),
  findingId: z.string().nullable(),
  checklistReferenceId: z.string().nullable(),
  materialRevisionId: z.string().nullable(),
  analysisRunId: z.string().nullable(),
  diagnostics: supportDiagnosticsSchema,
  /** Null on tickets with no review anchored, and on those raised before this existed. */
  disputedResult: supportDisputedResultSchema.nullable(),
  messages: z.array(supportMessageSchema),
});
export type SupportTicketDetail = z.infer<typeof supportTicketDetailSchema>;

/**
 * How a ticket list is ordered. `PRIORITY` is the inbox work-queue order
 * (blocking first, then oldest); the others exist because a client reading
 * their own history wants it newest-first, not triage-first.
 */
export const supportTicketSortSchema = z
  .enum(["PRIORITY", "NEWEST", "OLDEST", "RECENTLY_UPDATED", "SEVERITY", "STATUS"])
  .default("PRIORITY");
export type SupportTicketSort = z.infer<typeof supportTicketSortSchema>;

export const listSupportTicketsQuerySchema = z.object({
  cursor: z.string().optional(),
  sort: supportTicketSortSchema,
  limit: z.coerce.number().int().min(1).max(100).default(25),
  status: supportTicketStatusSchema.optional(),
  kind: supportTicketKindSchema.optional(),
  severity: supportSeveritySchema.optional(),
  reason: supportReportReasonSchema.optional(),
  organizationId: z.string().optional(),
  /** Inbox default: everything that is not RESOLVED or WONT_FIX. */
  openOnly: z
    .union([z.boolean(), z.enum(["true", "false"])])
    .transform((value) => value === true || value === "true")
    .optional(),
  search: z.string().max(200).optional(),
});
export type ListSupportTicketsQuery = z.input<typeof listSupportTicketsQuerySchema>;

export const supportTicketListPageSchema = z.object({
  items: z.array(supportTicketSummarySchema),
  nextCursor: z.string().nullable(),
});
export type SupportTicketListPage = z.infer<typeof supportTicketListPageSchema>;

export const updateSupportTicketSchema = z
  .object({
    status: supportTicketStatusSchema.optional(),
    severity: supportSeveritySchema.optional(),
    /** Optional note stored on the close event when the status becomes resolved. */
    comment: z.string().trim().max(4_000).optional(),
  })
  .refine((value) => value.status !== undefined || value.severity !== undefined, {
    message: "Provide a status or a severity to change.",
  });
export type UpdateSupportTicketInput = z.infer<typeof updateSupportTicketSchema>;

/** What a reporter or org Admin may set from the tenant Support page. */
export const closeSupportTicketSchema = z.object({
  status: z.enum(["RESOLVED", "WONT_FIX"]),
  comment: z.string().trim().max(4_000).optional(),
});
export type CloseSupportTicketInput = z.infer<typeof closeSupportTicketSchema>;

/**
 * Canonical body written into the thread when a ticket is closed, so the UI
 * can localise "who closed it, and when" instead of parsing prose. A closing
 * note, if any, follows the token on the next lines.
 */
export const SUPPORT_CLOSE_EVENT_BODY = {
  RESOLVED: "TICKET_CLOSED:RESOLVED",
  WONT_FIX: "TICKET_CLOSED:WONT_FIX",
} as const;

export function parseSupportCloseEvent(body: string): {
  status: "RESOLVED" | "WONT_FIX";
  comment: string | null;
} | null {
  const newline = body.indexOf("\n");
  const token = newline === -1 ? body : body.slice(0, newline);
  const status =
    token === SUPPORT_CLOSE_EVENT_BODY.RESOLVED
      ? "RESOLVED"
      : token === SUPPORT_CLOSE_EVENT_BODY.WONT_FIX
        ? "WONT_FIX"
        : null;
  if (!status) return null;
  const comment = newline === -1 ? "" : body.slice(newline + 1).trim();
  return { status, comment: comment.length > 0 ? comment : null };
}

export function supportCloseEventBody(
  status: "RESOLVED" | "WONT_FIX",
  comment?: string,
): string {
  const note = comment?.trim();
  return note ? `${SUPPORT_CLOSE_EVENT_BODY[status]}\n${note}` : SUPPORT_CLOSE_EVENT_BODY[status];
}

/** Counts for the Super Admin landing card, so the inbox advertises its backlog. */
export const supportInboxSummarySchema = z.object({
  open: z.number().int().nonnegative(),
  blocking: z.number().int().nonnegative(),
  awaitingFirstResponse: z.number().int().nonnegative(),
  oldestOpenSeconds: z.number().int().nonnegative().nullable(),
});
export type SupportInboxSummary = z.infer<typeof supportInboxSummarySchema>;

/**
 * What the report dialog asks for before the reporter submits, so it can show
 * the real values it is about to attach instead of a generic promise. It is the
 * same bundle `collectDiagnostics` stores on the ticket, read through the same
 * code path — the reporter sees what a Super Admin will see.
 */
export const supportReportContextQuerySchema = z.object({
  reviewId: z.string().min(1),
  findingId: z.string().min(1).optional(),
  checklistReferenceId: z.string().min(1).optional(),
  pagePath: z.string().max(500).optional(),
});
export type SupportReportContextQuery = z.infer<typeof supportReportContextQuerySchema>;

export const supportReportContextSchema = z.object({
  diagnostics: supportDiagnosticsSchema,
  disputedResult: supportDisputedResultSchema.nullable(),
  reviewId: z.string().nullable(),
  materialRevisionId: z.string().nullable(),
  analysisRunId: z.string().nullable(),
  findingId: z.string().nullable(),
  checklistReferenceId: z.string().nullable(),
});
export type SupportReportContext = z.infer<typeof supportReportContextSchema>;

/**
 * What a Super Admin reads live when triaging a ticket: the material the AI was
 * given, the result it produced, and the rule set that was frozen for the run.
 *
 * Read on demand rather than stored on the ticket, and audited on every read.
 * The reasoning is that this is the client's own advertising copy and the full
 * output of their review — copying it into a cross-tenant support table at
 * report time would make every ticket a permanent extract of tenant content,
 * whereas reading it here leaves the material where it already lives and puts
 * each access on the record.
 */
export const supportInspectionFindingSchema = z.object({
  id: z.string(),
  title: z.string(),
  explanation: z.string(),
  severity: z.string(),
  confidence: z.string().nullable(),
  detectionMethod: z.string(),
  evidenceQuote: z.string(),
  evidenceType: z.string(),
  suggestedRevision: z.string().nullable(),
  citationType: z.string(),
  citationTitle: z.string(),
  citationLocator: z.string().nullable(),
  pageNumber: z.number().int().nullable(),
  ruleVersionId: z.string().nullable(),
  policyVersionId: z.string().nullable(),
  /** True for the finding this ticket disputes, so it can be found in a long list. */
  disputed: z.boolean(),
});
export type SupportInspectionFinding = z.infer<typeof supportInspectionFindingSchema>;

export const supportInspectionChecklistItemSchema = z.object({
  referenceId: z.string(),
  title: z.string().nullable(),
  verdict: z.string().nullable(),
  note: z.string().nullable(),
  disputed: z.boolean(),
});
export type SupportInspectionChecklistItem = z.infer<typeof supportInspectionChecklistItemSchema>;

export const supportInspectionSourceSchema = z.object({
  id: z.string(),
  citationType: z.string(),
  title: z.string(),
  ruleVersionId: z.string().nullable(),
  policyVersionId: z.string().nullable(),
});
export type SupportInspectionSource = z.infer<typeof supportInspectionSourceSchema>;

export const supportInspectionSchema = z.object({
  reviewId: z.string(),
  reviewTitle: z.string(),
  organizationName: z.string(),
  /** The revision the ticket named, which a later resubmission may have superseded. */
  revisionId: z.string().nullable(),
  revisionNumber: z.number().int().nullable(),
  fileName: z.string().nullable(),
  processingStatus: z.string().nullable(),
  /** Exactly the text the deterministic checks and the model were given. */
  bodyText: z.string().nullable(),
  bodyTextTruncated: z.boolean(),
  /** Whether the original upload can still be streamed for a visual check. */
  hasFile: z.boolean(),
  /** False once a later revision exists: the review has moved past this ticket. */
  isCurrentRevision: z.boolean(),
  analysisRunId: z.string().nullable(),
  analysisStatus: z.string().nullable(),
  analysisErrorMessage: z.string().nullable(),
  aiSchemaValid: z.boolean().nullable(),
  rejectedFindingCount: z.number().int().nullable(),
  riskStatus: z.string().nullable(),
  aiSummary: z.string().nullable(),
  materialOverview: z.string().nullable(),
  recommendedAction: z.string().nullable(),
  startedAt: isoDate.nullable(),
  finishedAt: isoDate.nullable(),
  checklist: z.array(supportInspectionChecklistItemSchema),
  findings: z.array(supportInspectionFindingSchema),
  sources: z.array(supportInspectionSourceSchema),
  /**
   * False when the disputed finding is no longer in the run — a rerun deletes
   * and recreates findings, so the frozen copy on the ticket is then the only
   * record of what the client actually saw.
   */
  disputedFindingPresent: z.boolean(),
});
export type SupportInspection = z.infer<typeof supportInspectionSchema>;
